package com.neurobs.ccabserver.administrator.controller

import com.neurobs.ccab.{ReleaseUpdateData, VersionNumber, ReleaseDataRest}
import com.neurobs.ccab.db.ReleaseTypeData
import com.neurobs.ccabserver.administrator.view.ReleaseListView
import com.neurobs.js.ccab.CCABServer
import com.neurobs.js.controller.{AdminListAdapter, AdminListController, AdminPanelDelegate}
import com.neurobs.js.dom.RecordTableView._
import com.neurobs.js.util.StringUtilities._
import com.neurobs.js.view.{Container, Form, Gui, MessageBox, MessageBoxResult, ViewElement}

import scala.concurrent.{Future, Promise}

class ReleaseListController (gui: Gui, releaseType: ReleaseTypeData) extends AdminListAdapter[ReleaseDataRest] {

	override def title = s"${releaseType.displayName} Releases"

	override def itemName = "Release"

	override def columnHeaders = Seq(
		"Version",
		"Created",
		"Release Date",
		"Active",
		"URL",
		s"Min ${releaseType.dependency1Description.getOrElse("Dep")}",
		"Build ID",
		"",
		"",
		""
	)

	override def itemRow(r: ReleaseDataRest, delegate: Option[AdminPanelDelegate], controller: AdminListController[ReleaseDataRest]) = Seq(
			r.version,
			dateString(r.createdAt),
			r.releaseDate.map(_.toString).getOrElse[String](default = ""),
			if (r.active) "Yes" else "No",
			r.url.map(u => ExternalLinkCell("Download", u)).getOrElse(TextCell("")),
			r.dependency1MinVersion,
			r.buildId.map(_.noBracketsString).getOrElse[String](default = ""),
			CommandLinkCell("edit", () => editRelease(r, controller)),
			CommandLinkCell("notes", () => displayNotes(r, controller)),
			CommandLinkCell("delete", () => deleteRelease(r, controller))
		)

	override val create = Some(createRelease)

	override def get(): Future[Either[String, Seq[ReleaseDataRest]]] = {
		val promise = Promise[Either[String, Seq[ReleaseDataRest]]]
		CCABServer.getReleases(releaseType.identifier, all = true) {
			case Left(error) => promise.success(Left(error.message))
			case Right(data) => promise.success(Right(data))
		}
		promise.future
	}

	private val view = new ReleaseListView(releaseType)

	private def editRelease(r: ReleaseDataRest, c: AdminListController[ReleaseDataRest]): Unit =  {
		val form = view.makeUpdateForm(r)

		form.activeBox.setChecked(r.active)
		form.activeBox.setReadOnly(r.url.isEmpty)
		form.notesBox.setValue(r.releaseNotes)
		form.minDependencyBox.setValue(r.dependency1MinVersion)
		form.releaseDateBox.setValue(r.releaseDate.map(_.toString).getOrElse(""))

		def process(callback: (Option[String]) => Unit): Either[String,Form.Process] = {
			def getDependency: Either[String, String] = {
				val minDep = form.minDependencyBox.value()
				if ((!minDep.isEmpty) && VersionNumber(minDep).isEmpty) Left("Version numbers must be in the format 'n.nnn.nnn' with each number < 256.")
				else Right(minDep)
			}
			for (
				minDep <- getDependency;
				releaseDate <- ViewElement.getOptionalDate(form.releaseDateBox, name = "release date")
			) yield  {
				val data = ReleaseUpdateData(
					active = form.activeBox.checked,
					dependency1MinVersion = minDep,
					releaseNotes = form.notesBox.value(),
					releaseDate = releaseDate
				)
				CCABServer.updateRelease(r.id, data) {
					case Some(error) => callback(Some(error.message))
					case None =>
						callback(None)
						c.getList()
				}
			}
		}

		gui.showModalForm(form.form, process, true, Some(() => Unit))
	}

	private def deleteRelease(r: ReleaseDataRest, c: AdminListController[ReleaseDataRest]): Unit =  {
		val confirm = gui.makeMessageBox(40, s"Delete Version ${r.version} Confirmation", s"This will permanently delete version ${r.version} from the server and is not recoverable.", List(("Delete", MessageBoxResult.Ok), ("Cancel", MessageBoxResult.Cancel)))
		MessageBox.showModal(confirm, gui.modal) {
			case MessageBoxResult.Ok =>
				CCABServer.deleteRelease(r.id) {
					case Some(error) => c.setError(error.message)
					case None => c.getList()
				}
			case _ =>
		}
	}

	private def displayNotes(r: ReleaseDataRest, c: AdminListController[ReleaseDataRest]): Unit =  {
		val dialog = view.makeNotesDialog(r.version, r.releaseNotes)
		MessageBox.showModal(dialog, gui.modal)((_) => Unit)
	}

	private def createRelease(c: AdminListController[ReleaseDataRest]): Unit = {
		val form = view.makeUploadForm()

		def process(callback: (Option[String]) => Unit): Either[String,Form.Process] = {
			val ver = form.versionBox.value()
			val minDep = form.minDependencyBox.value()

			if (VersionNumber(ver).isEmpty) Left("Version numbers must be in the format 'n.nnn.nnn'.")
			else if ((!minDep.isEmpty) && VersionNumber(minDep).isEmpty) Left("Version numbers must be in the format 'n.nnn.nnn' with each number < 256.")
			else if (form.fileBox.value.isEmpty) Left("Please select a file.")
			else {
				val file = form.fileBox.value.get
				val fileParts = file.name.split("\\.")
				if (fileParts.length == 1) Left("The filename must have an extension.")
				else {
					val fileExtension = fileParts.last
					Right(
						CCABServer.createRelease(releaseType.identifier, ver, minDep, fileExtension, form.fileBox.value.get) {
							case Left(error) => callback(Some(error.message))
							case Right(res) => res.error match {
								case Some(error) => callback(Some(error))
								case None =>
									callback(None)
									c.getList()
							}
						}
					)
				}
			}
		}

		gui.showModalForm(form.form, process, true, Some(() => Unit))
	}
}

object ReleaseListController {
	def apply(gui: Gui, releaseType: ReleaseTypeData): AdminListController[ReleaseDataRest] = new AdminListController[ReleaseDataRest](new ReleaseListController(gui, releaseType))
}